import { useEffect, useRef } from "react";
import { DEFAULT_CAM } from "@/components/editor/FramingPanel";

// Draws the source video into the output frame exactly like the server framing (crop / blur / bars / split).
const cover = (ctx, v, [sx, sy, sW, sH], [dx, dy, dW, dH], cx = 0.5, cy = 0.5, z = 1) => {
  const s = Math.max(dW / sW, dH / sH) * Math.max(1, z);
  const cw = dW / s, ch = dH / s;
  ctx.drawImage(v, sx + (sW - cw) * cx, sy + (sH - ch) * cy, cw, ch, dx, dy, dW, dH);
};

const contain = (ctx, v, [sx, sy, sW, sH], [dx, dy, dW, dH], z = 1) => {
  const s = Math.min(dW / sW, dH / sH) * Math.max(1, z);
  const w = sW * s, h = sH * s;
  ctx.save();
  ctx.beginPath();
  ctx.rect(dx, dy, dW, dH);
  ctx.clip();
  ctx.drawImage(v, sx, sy, sW, sH, dx + (dW - w) / 2, dy + (dH - h) / 2, w, h);
  ctx.restore();
};

const blurred = (ctx, v, src, dst) => {
  ctx.filter = "blur(18px) brightness(0.62)";
  cover(ctx, v, src, dst);
  ctx.filter = "none";
};

export const drawFrame = (ctx, v, cfg, W, H) => {
  const sw = v.videoWidth, sh = v.videoHeight;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, W, H);
  if (!sw || !sh) return;
  const full = [0, 0, sw, sh];
  const z = cfg.zoom || 1;
  if (cfg.fit === "split" && cfg.aspect === "9:16") {
    const hc = Math.round(H * (cfg.split_ratio ?? 0.35));
    const [x, y, w, h] = cfg.cam_rect || DEFAULT_CAM;
    const cam = [x * sw, y * sh, Math.max(2, w * sw), Math.max(2, h * sh)];
    const top = [0, 0, W, hc];
    if (cam[2] / cam[3] < 0.75 * (W / hc) && (cfg.cam_zoom || 1) <= 1) { blurred(ctx, v, cam, top); contain(ctx, v, cam, top); }
    else cover(ctx, v, cam, top, cfg.cam_x ?? 0.5, cfg.cam_y ?? 0.5, cfg.cam_zoom || 1);
    cover(ctx, v, full, [0, hc, W, H - hc], cfg.crop_x ?? 0.5, cfg.crop_y ?? 0.5, z);
    return;
  }
  if (cfg.fit === "crop") return cover(ctx, v, full, [0, 0, W, H], cfg.crop_x ?? 0.5, cfg.crop_y ?? 0.5, z);
  if (cfg.fit === "blur") blurred(ctx, v, full, [0, 0, W, H]);
  contain(ctx, v, full, [0, 0, W, H], z);
};

export const ResultCanvas = ({ vref, cfg, style }) => {
  const cv = useRef(null);
  const cfgRef = useRef(cfg);
  useEffect(() => { cfgRef.current = cfg; }, [cfg]);
  useEffect(() => {
    let raf;
    const loop = () => {
      const c = cv.current, v = vref.current;
      if (c && v && v.readyState >= 2) {
        const r = c.getBoundingClientRect();
        const W = Math.min(720, Math.round(r.width * (window.devicePixelRatio || 1))) || 360;
        const H = Math.round(W * (r.height / Math.max(1, r.width)));
        if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
        drawFrame(c.getContext("2d"), v, cfgRef.current, W, H);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [vref]);
  return <canvas ref={cv} className="absolute inset-0 w-full h-full" style={style} data-testid="result-canvas" />;
};
